import { Box, Flex, TabsProps as ChakraTabsProps } from '@chakra-ui/react';
import { useEffect, useMemo } from 'react';
import EmailTemplatesTable from './EmailTemplatesTable';
import { Tabs } from '@morressier/alembic';
import EmailCategoriesTable from './EmailCategoriesTable';
import ErrorsLogTable from './ErrorsLogTable';
import EmailLogsTable from './EmailLogsTable';
import { useWorkspace } from '../../SubmissionContexts/Users/userHook';
import ScrollToTopButton from 'src/pages/Organization/public/Journal/SubmissionUpload/components/common/ScrollToTopButton';
import Footer from '../../../../../components/Footer';
import { useNavigate, useParams } from 'react-router-dom';
import useAuthAccessQuery from 'src/hooks/useAuthAccessQuery';

export const EMAILS_TABS = {
  TEMPLATES: 'Email Templates',
  EMAILS_TAGS: 'Email Tags',
  ALL_EMAIL_LOGS: 'All Email Logs',
  ERRORS_LOG: 'Email Error Logs'
} as const;

export type EmailsTab = (typeof EMAILS_TABS)[keyof typeof EMAILS_TABS];

const EMAILS_TAB_ROUTES: Record<EmailsTab, string> = {
  [EMAILS_TABS.TEMPLATES]: 'templates',
  [EMAILS_TABS.EMAILS_TAGS]: 'email-tags',
  [EMAILS_TABS.ALL_EMAIL_LOGS]: 'all-email-logs',
  [EMAILS_TABS.ERRORS_LOG]: 'email-error-logs'
};

type RenderTabContent = {
  activeTab: EmailsTab;
  organizationId: string;
};

export const renderTabContent = ({
  activeTab,
  organizationId
}: RenderTabContent) => {
  switch (activeTab) {
    case EMAILS_TABS.TEMPLATES:
      return <EmailTemplatesTable organizationId={organizationId} />;
    case EMAILS_TABS.EMAILS_TAGS:
      return <EmailCategoriesTable organizationId={organizationId} />;
    case EMAILS_TABS.ALL_EMAIL_LOGS:
      return <EmailLogsTable />;
    case EMAILS_TABS.ERRORS_LOG:
      return <ErrorsLogTable />;
    default:
      return null;
  }
};
const ManageEmails = () => {
  const { emailTab } = useParams();
  const navigate = useNavigate();
  const { orgId, workspaceId } = useWorkspace();
  const tabs = useMemo(() => Object.values(EMAILS_TABS), []);
  const activeTabIndex = Math.max(
    tabs.findIndex(tab => EMAILS_TAB_ROUTES[tab] === emailTab),
    0
  );
  const activeTab = tabs[activeTabIndex] ?? EMAILS_TABS.TEMPLATES;

  useEffect(() => {
    if (EMAILS_TAB_ROUTES[activeTab] !== emailTab) {
      navigate(EMAILS_TAB_ROUTES[activeTab], { replace: true });
    }
  }, [activeTab, emailTab, navigate]);

  const { isAllowed: canManage } = useAuthAccessQuery({
    relation: 'team_manager',
    entityType: 'workspace',
    entityId: workspaceId
  });
  const { isAllowed: canView } = useAuthAccessQuery({
    relation: 'canView',
    entityType: 'workspace',
    entityId: workspaceId
  });

  const getVisibleTabs = useMemo(() => {
    if (canManage) {
      return Object.values(EMAILS_TABS);
    }
    if (canView) {
      return [EMAILS_TABS.ALL_EMAIL_LOGS, EMAILS_TABS.ERRORS_LOG];
    }
    return [];
  }, [canManage, canView]);

  const chakraTabsProps = useMemo(
    () =>
      ({
        index: activeTabIndex,
        onChange: (index: number) => {
          const nextTab = tabs[index];

          if (!nextTab) return;

          navigate(EMAILS_TAB_ROUTES[nextTab]);
        }
      } as ChakraTabsProps),
    [activeTabIndex, navigate, tabs]
  );

  return (
    <Box id="scrollable-wrapper" overflowY="auto" height="100%">
      <Box height="100%">
        <Box padding={5} minHeight="100%">
          <Flex direction="column" gap={3}>
            <Tabs
              chakraTabsProps={chakraTabsProps}
              tabs={getVisibleTabs.map((tab, index) => ({
                label: tab,
                content:
                  activeTabIndex === index
                    ? renderTabContent({
                        activeTab: tab,
                        organizationId: orgId
                      })
                    : null
              }))}
            />
          </Flex>
        </Box>
        <Footer />
      </Box>
      <ScrollToTopButton />
    </Box>
  );
};

export default ManageEmails;
